import type { ReportingDatePreset } from '../../types/reporting.js';
import { presetToRange, toLocalYmd, type DateRange } from '../reportingDateRange.js';

// CLIENT PORTAL RESTYLE — the report's default period.
//
// ⚠️⚠️ THIS MONTH TO DATE, EXCLUDING TODAY. Not last full month (the Phase-1
// default), not a rolling 30 days. Excluding today matters and is the whole
// reason this file exists: today is always partial, so including it drags every
// average down by however many hours are left in the day — differently depending
// on when the client opens the page, which is what makes it near-impossible to
// spot by eye.
//
// ⚠️⚠️ `presetToRange` IS NOT FORKED. Its `this_month` deliberately runs to the
// local today (month-to-date is the convention every staff surface uses, and the
// Meta pull window is padded to match it — see reportingDateRange.ts's header).
// Changing it here would move the AM's day off their own calendar. So the shared
// resolver stays authoritative and this narrows its END, once, with the
// narrowing stated.

/** The preset the report opens on. */
export const DEFAULT_PORTAL_PRESET: ReportingDatePreset = 'this_month';

/**
 * ⚠️⚠️ HOW MANY COMPLETE DAYS THIS MONTH MUST HOLD BEFORE THE REPORT OPENS ON
 * IT. Scott, 2026-09-02: fall back to last month early in the month.
 *
 * ONE SHARED CONSTANT, and that is the point — a 3 in the resolver and another 3
 * in a test is two things to keep in step, and the test would then pass by
 * agreeing with itself rather than with the code.
 */
export const MIN_COMPLETE_DAYS_FOR_THIS_MONTH = 3;

/**
 * Which preset the report OPENS on.
 *
 * ⚠️⚠️ WHY THERE IS A RULE AT ALL. `presetToRange('this_month')` runs from the
 * 1st to the client's local TODAY — it INCLUDES today. So on the 1st it is a
 * one-day range consisting only of today, and today holds no data until UTC
 * midnight (_architect/findings/2026-08-27-reporting-today-empty-until-utc-
 * midnight.md). A client opening the report on the 1st would get an all-dashes
 * page that reads as broken software rather than as an early month.
 *
 * ⚠️ MEASURED IN COMPLETE DAYS — days strictly BEFORE today — not in the date
 * number. They happen to coincide (day N has N−1 complete days behind it), but
 * "complete days" is the property that matters and the one a reader can check.
 *
 * ⚠️⚠️ THIS IS A DEFAULT, NOT A RESTRICTION. A client who explicitly picks "This
 * month so far" on the 2nd gets this month, one day and all. This decides only
 * where the report opens.
 *
 * ⚠️ THE CONTROL MUST SHOW THE RESOLVED PRESET. A default period that silently
 * changes on the 4th of the month is the same class of problem as a silent zero,
 * so the dropdown reads "Last month" on the 2nd rather than "This month so far"
 * over last month's figures.
 */
export function resolveDefaultPortalPreset(now: Date): ReportingDatePreset {
  // `getDate()` is 1-based, so the 1st has 0 complete days behind it.
  const completeDaysThisMonth = now.getDate() - 1;
  return completeDaysThisMonth >= MIN_COMPLETE_DAYS_FOR_THIS_MONTH
    ? DEFAULT_PORTAL_PRESET
    : 'last_month';
}

/**
 * The default range: this month so far, ending YESTERDAY.
 *
 * ⚠️ ON THE FIRST OF A MONTH there is no month-to-date left once today is
 * removed, so it falls back to the whole PREVIOUS month — a complete window, and
 * the question a client opening the report on the 1st is actually asking. An
 * inverted or single-day range would render an empty report on one day in
 * thirty, which reads as the tool being broken.
 */
export function defaultPortalRange(now: Date): { range: DateRange; preset: ReportingDatePreset } {
  const monthToDate = presetToRange('this_month', now);
  const yesterday = toLocalYmd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  if (yesterday < monthToDate.from) {
    return { range: presetToRange('last_month', now), preset: 'last_month' };
  }
  return { range: { from: monthToDate.from, to: yesterday }, preset: DEFAULT_PORTAL_PRESET };
}

/**
 * Apply the exclude-today narrowing to any month-to-date-shaped preset.
 *
 * ⚠️ ONLY the presets that RUN TO TODAY are narrowed. `last_month` and the
 * `past_N` windows already end yesterday by their own definition, and re-ending
 * them here would silently make "past 30 days" mean 29 — the same class of
 * mistake as `includeToday` keying on dates rather than on the preset (see the
 * lead-bot report note).
 */
const RUNS_TO_TODAY: ReadonlySet<ReportingDatePreset> = new Set<ReportingDatePreset>([
  'this_week',
  'this_month',
  'this_quarter',
  'this_year',
]);

export function portalRangeFor(
  preset: ReportingDatePreset,
  now: Date,
  custom?: DateRange,
): DateRange {
  if (preset === 'custom') {
    if (!custom) return defaultPortalRange(now).range;
    // ⚠️ A CUSTOM range is the client's own selection and is left exactly as
    // chosen. Silently trimming a date somebody typed is worse than showing them
    // a partial day they asked for.
    return custom;
  }
  const range = presetToRange(preset, now);
  if (!RUNS_TO_TODAY.has(preset)) return range;
  const yesterday = toLocalYmd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  if (yesterday < range.from) return defaultPortalRange(now).range;
  return { from: range.from, to: yesterday };
}

/**
 * The presets the client may pick.
 *
 * ⚠️ DELIBERATELY SHORT. The staff dashboards offer fourteen, including today /
 * yesterday / this-week — operational questions an AM asks mid-campaign, not
 * reporting periods. `this_month` leads because it is the default, and its label
 * says the exclusion out loud rather than leaving it to be discovered.
 */
export const RESTYLE_PRESETS: ReadonlyArray<{ value: ReportingDatePreset; label: string }> = [
  { value: 'this_month', label: 'This month to date' },
  { value: 'last_month', label: 'Last full month' },
  { value: 'past_30', label: 'Last 30 days' },
  { value: 'past_90', label: 'Last 90 days' },
  { value: 'this_year', label: 'Year to date' },
  { value: 'custom', label: 'Custom…' },
];
